import React from "react";
import { CircularProgress } from "@mui/material";
import cancelOrderIcon from "../../assets/images/svg/cancel-order.svg";
import warnOrderIcon from "../../assets/images/svg/warn.svg";

type PlanType = "monthly" | "yearly";

interface MetaAdsChangePlanModalProps {
  isOpen: boolean;
  currentPlan: PlanType;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

const planConfig = {
  monthly: {
    price: "$9",
    period: "/month",
    gradient: "linear-gradient(270deg, #FFAA2D 0%, #A95A00 100%)",
    border: "#FF7B00",
    label: "MONTHLY",
  },
  yearly: {
    price: "$100",
    period: "/year",
    gradient: "linear-gradient(270deg, #58F2A9 0%, #1C3941 100%)",
    border: "#58F2A9",
    label: "YEARLY",
  },
};

export default function MetaAdsChangePlanModal({
  isOpen,
  currentPlan,
  onClose,
  onConfirm,
  isLoading = false,
}: MetaAdsChangePlanModalProps) {
  if (!isOpen) return null;

  const targetPlan: PlanType = currentPlan === "monthly" ? "yearly" : "monthly";
  const current = planConfig[currentPlan];
  const target = planConfig[targetPlan];

  return (
    <div className="fixed inset-0 z-[10000] bg-black/75 backdrop-blur-md flex items-center justify-center lg:p-4 p-2 sm:p-6 overflow-y-auto">
      <div className="relative w-full max-w-[480px] sm:max-w-[800px] py-[10px] px-[20px] my-auto rounded-[30px] sm:rounded-[40px] overflow-hidden bg-[#0e0e10]">
        {/* Background gradients */}
        <div
          className="absolute inset-x-0 top-0 h-[220px] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 65% at 60% 0%, rgba(27,118,255,0.85) 0%, rgba(27,118,255,0.32) 42%, rgba(9,11,18,0) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(6,13,30,0.38) 0%, rgba(9,11,18,0) 32%, rgba(9,11,18,0.35) 100%)",
          }}
        />

        <div className="relative z-10 lg:px-3   pt-7 pb-7 sm:pb-[34px]">
          {/* Header */}
          <div className="relative mb-5">
            {/* Desktop Layout */}
            <div className="flex items-center justify-between">
              <h2 className="text-white text-[24px] lg:text-[48px] font-semibold">
                Change Plan
              </h2>

              <button
                type="button"
                onClick={onClose}
                aria-label="Close cancel plan modal"
                className="hidden sm:block text-white lg:text-[32px] text-[24px] mt-[-7px] lg:mt-0 leading-none mt-1 flex-shrink-0"
                disabled={isLoading}
              >
                ✕
              </button>
            </div>

            {/* Mobile Close (Top Right Corner) */}
            <button
              onClick={onClose}
              aria-label="Close order modal"
              className="sm:hidden absolute top-[-12px] right-[-2px]"
            >
              <img
                src={cancelOrderIcon}
                alt="Close Icon"
                className="w-[20px] h-[20px]"
              />
            </button>
          </div>

          <div className="py-8 lg:space-y-8 space-y-4">
            {/* Current Plan Label */}
            <p className="text-white text-[16px] font-medium">
              You are currently on:
            </p>

            {/* Current Plan Card */}
            <div
              className="lg:rounded-full rounded-[32px] px-[32px] py-[16px] flex flex-col lg:flex-row lg:items-center lg:justify-between justify-center"
              style={{
                background: current.gradient,
                border: `1px solid ${current.border}`,
              }}
            >
              <span className="text-white lg:text-[18px] text-[14px] font-medium">
                Full Meta Ads Builder Access
              </span>
              <span className="text-white font-medium leading-none mt-1 lg:mt-0">
                <span className="text-[32px]">{current.price}</span>
                <span className="text-[20px]">{current.period}</span>
              </span>
            </div>

            {/* Target Plan Label */}
            <p className="text-white text-[16px] font-medium">
              Are you sure you want to change plan to {target.label}?
            </p>

            {/* Target Plan Card */}
            <div
              className="lg:rounded-full rounded-[32px] px-[32px] py-[16px] flex flex-col lg:flex-row lg:items-center lg:justify-between justify-center"
              style={{
                background: target.gradient,
                border: `1px solid ${target.border}`,
              }}
            >
              <span className="text-white lg:text-[18px] text-[14px] font-medium">
                Full Meta Ads Builder Access
              </span>
              <span className="text-white font-medium leading-none mt-1 lg:mt-0">
                <span className="text-[32px]">{target.price}</span>
                <span className="text-[20px]">{target.period}</span>
              </span>
            </div>
          </div>

          {/* Warning Box */}
          <div className="rounded-[14px] bg-white/[0.06] p-4 sm:p-[22px] flex items-start  gap-3 leading-[140%]">
            <div className="flex-shrink-0">
              <img
                src={warnOrderIcon}
                alt="Warning Icon"
                className="w-[28px] h-[28px] sm:w-[33px] sm:h-[33px]"
              />
            </div>

            <p className="text-white text-[12px] lg:text-[16px] font-medium">
              Changing your plan will cancel your current subscription and start
              the new plan with immediate payment.
            </p>
          </div>

          {/* Divider */}
          <div className="w-full h-px bg-white/12 mt-5 sm:mt-8" />

          {/* Buttons */}
          <div
            className="
    mt-5 sm:mt-8
    flex flex-col items-center gap-3
    sm:flex-row sm:justify-center sm:gap-4
  "
          >
            {/* Cancel */}
            <button
              type="button"
              onClick={onClose}
              className="
      order-2 sm:order-1
      w-[70%] sm:w-auto
      px-[32px] py-[14px]
      rounded-full
      bg-white text-black
      text-[15px] sm:text-[16px]
      font-semibold
      hover:bg-white/90
      transition-all duration-200
    "
            >
              Cancel
            </button>

            {/* Confirm */}
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="
      order-1 sm:order-2
      w-full sm:w-auto
      px-[32px] py-[14px]
      rounded-full
      text-white
      text-[15px] sm:text-[16px]
      font-semibold
      disabled:opacity-70
      disabled:cursor-not-allowed
      hover:opacity-90
      transition-all duration-200
    "
              style={{
                background:
                  "linear-gradient(272.84deg, #58F2A9 0.97%, #1C3941 99.18%)",
                border: "1px solid #58F2A9",
              }}
            >
              {isLoading ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                  Processing...
                </span>
              ) : (
                "Confirm Change"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
